import { supabase } from '../../lib/supabase'

export type DecisionKind = 'approve' | 'return' | 'deny' | 'approve_modified'

export type DecideLeaveRequestParams = {
  leaveRequestId: string
  decision: DecisionKind
  comment?: string
  // Approve with supervisor modification only: the subset of the
  // request's own dates the supervisor actually approved, per
  // docs/design/journey-map.md's fourth decision.
  modifiedDates?: string[]
}

// The only path to approve, return, deny, or approve with modification:
// v1-decide-leave-request (supabase/functions/v1-decide-leave-request)
// records the decision and moves the leave_requests row's own state in
// one transaction while holding the service role, per
// docs/architecture/data-model.md's rule for a write that carries meaning
// beyond a single row. Returns whether it landed, the same as this
// file's own former sessionStorage version.
//
export async function decideLeaveRequest(params: DecideLeaveRequestParams): Promise<boolean> {
  const { error } = await supabase.functions.invoke('v1-decide-leave-request', { body: params })
  return !error
}

export type ReverseLeaveDecisionParams = { leaveRequestId: string; reason: string }

// Takes back an approval or a denial, returning the request to pending for a
// new decision. v1-reverse-leave-decision refuses a request that is not
// approved or denied and one whose leave has ended, so this reports the
// refusal's own wording where there is one. Returns whether it landed.
export async function reverseLeaveDecision(params: ReverseLeaveDecisionParams): Promise<{ ok: true } | { ok: false; message: string }> {
  const { error } = await supabase.functions.invoke('v1-reverse-leave-decision', { body: params })
  if (!error) return { ok: true }

  let message = error.message
  const context = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context
  if (context?.json) {
    try {
      const body = await context.json()
      if (body.error) message = body.error
    } catch {
      // The body was not JSON; the error's own message stands.
    }
  }
  return { ok: false, message: message || 'The decision could not be reversed. Try again.' }
}
